import { collectionKey, pageUrl, cachePage, readPage, savedPosition, savePosition, restorePosition } from './akbar-infinite-state.js?v=a3c2e37ae17c';
import { installControls, updateControls, appendProducts, prependProducts } from './akbar-infinite-view.js?v=174a156af410';

class AkbarInfinitePagination extends HTMLElement {
  connectedCallback() {
    if (this.events || !window.IntersectionObserver) return;
    this.grid = this.parentElement.querySelector('.product-list');
    if (!this.grid) return;
    this.key = collectionKey();
    this.firstPage = this.page = Number(this.dataset.page);
    this.next = this.dataset.next;
    // Rendered above the grid only when the visitor lands on page 2 or later.
    this.earlier = this.parentElement.querySelector('[data-earlier]');
    this.paused = this.failed = this.loading = this.loadingEarlier = this.restoring = false;
    this.events = new AbortController();
    this.requests = new AbortController();
    this.seen = new Set([...this.grid.children].map(cell => cell.dataset.productId));
    [...this.grid.children].forEach(cell => { cell.dataset.page = this.page; });
    cachePage(pageUrl(location.href, this.page), this.initialPage());
    installControls(this);
    this.bind();
    const saved = savedPosition(this.key);
    const restore = saved && saved.lastPage >= saved.firstPage && saved.lastPage <= Number(this.dataset.pages);
    this.ready = restore ? restorePosition(this, saved) : Promise.resolve();
    this.ready.then(() => { if (this.isConnected) this.observe(); });
    this.update();
  }

  initialPage() {
    return {
      html: this.grid.innerHTML, next: this.next, page: this.page, total: this.dataset.total,
      pagination: this.querySelector('[data-pagination]').innerHTML
    };
  }

  bind() {
    const options = { signal: this.events.signal };
    window.addEventListener('scroll', () => this.onScroll(), { ...options, passive: true });
    window.addEventListener('pagehide', () => savePosition(this), options);
    window.addEventListener('pageshow', event => {
      if (event.persisted) { this.requests = new AbortController(); this.update(); }
    }, options);
    document.addEventListener('click', event => {
      if (event.target.closest('product-card a')) savePosition(this);
    }, { ...options, capture: true });
    this.earlier?.addEventListener('click', event => {
      // Modified clicks keep the link's own behaviour, such as opening the page in a new tab.
      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.loadEarlier();
    }, options);
    document.addEventListener('facet:update', event => {
      if (event.detail.url.searchParams.get('section_id') !== this.dataset.section) return;
      // Stop this result set before Prestige replaces the URL and section.
      this.requests.abort();
      this.requests = new AbortController();
      this.paused = true;
    }, { ...options, capture: true });
  }

  observe() {
    this.observer = new IntersectionObserver(() => this.check(), { rootMargin: '0px 0px 1000px 0px' });
    this.observer.observe(this.querySelector('[data-sentinel]'));
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => savePosition(this), 150);
      this.check();
    });
  }

  check() {
    if (this.paused || this.failed || this.loading || this.restoring || !this.next) return;
    if (document.activeElement?.closest('[data-pagination]')) return;
    const edge = this.querySelector('[data-sentinel]').getBoundingClientRect().top;
    if (edge >= 0 && edge < innerHeight + 1000) this.load();
  }

  async fetchPage(input, useCache = false) {
    const url = new URL(input, location.href);
    if (url.origin !== location.origin || collectionKey(url) !== this.key) throw new Error('Invalid next page');
    const cacheUrl = pageUrl(url, Number(url.searchParams.get('page') || 1));
    const cached = useCache && readPage(cacheUrl);
    if (cached) return cached;
    url.searchParams.set('section_id', this.dataset.section);
    const controller = new AbortController();
    const abort = () => controller.abort();
    const parentSignal = this.requests.signal;
    if (parentSignal.aborted) throw new DOMException('Aborted', 'AbortError');
    parentSignal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 15000);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Page request failed');
      const result = this.parsePage(await response.text());
      if (result.page !== Number(url.searchParams.get('page') || 1)) throw new Error('Unexpected page');
      cachePage(cacheUrl, result);
      return result;
    } finally {
      clearTimeout(timer);
      parentSignal.removeEventListener('abort', abort);
    }
  }

  parsePage(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const controls = doc.querySelector('akbar-infinite-pagination');
    const grid = controls?.parentElement.querySelector('.product-list');
    if (!controls || !grid || !grid.children.length) throw new Error('Missing product grid');
    return {
      html: grid.innerHTML, next: controls.dataset.next,
      page: Number(controls.dataset.page), total: controls.dataset.total,
      pagination: controls.querySelector('[data-pagination]').innerHTML
    };
  }

  async load(manual = false) {
    if (this.loading || this.restoring || !this.next || !this.isConnected || collectionKey() !== this.key) return;
    this.loading = true;
    this.failed = false;
    this.message('Loading more designs…');
    this.update();
    const previous = this.grid.lastElementChild;
    try {
      const result = await this.fetchPage(this.next);
      if (!this.isConnected || collectionKey() !== this.key) return;
      if (result.page <= this.page) throw new Error('Repeated page');
      const added = this.appendPage(result);
      this.dataset.ready = '';
      this.message(added + ' more designs loaded.');
      if (manual) this.focusAdded(previous);
      savePosition(this);
    } catch (error) {
      if (error.name === 'AbortError' && this.paused) return;
      if (this.isConnected && collectionKey() === this.key) {
        this.failed = true;
        this.message('Couldn’t load more designs. Please retry or use the page links.');
        this.removeAttribute('data-ready');
      }
    } finally {
      this.loading = false;
      if (this.isConnected) this.update();
    }
  }

  // Loads the page before the first one shown, for visitors who landed mid-collection.
  async loadEarlier() {
    if (this.loadingEarlier || this.restoring || this.firstPage <= 1 || collectionKey() !== this.key) return;
    this.loadingEarlier = true;
    this.message('Loading earlier designs…');
    this.update();
    try {
      const result = await this.fetchPage(pageUrl(location.href, this.firstPage - 1), true);
      if (!this.isConnected || collectionKey() !== this.key) return;
      const cells = prependProducts(this, result);
      this.message(cells.length + ' earlier designs loaded.');
      cells[0]?.querySelector('a')?.focus({ preventScroll: true });
      savePosition(this);
    } catch (error) {
      if (error.name === 'AbortError' && this.paused) return;
      if (this.isConnected) this.message('Couldn’t load earlier designs. Please try again.');
    } finally {
      this.loadingEarlier = false;
      if (this.isConnected) this.update();
    }
  }

  appendPage(result) { return appendProducts(this, result); }

  update() {
    updateControls(this);
    // Without JavaScript handling the click, the link still opens the right earlier page.
    if (this.earlier && this.firstPage > 1) this.earlier.href = pageUrl(location.href, this.firstPage - 1);
  }

  message(text) { this.querySelector('[data-status]').textContent = text; }

  focusAdded(previous) {
    const link = previous?.nextElementSibling?.querySelector('a');
    if (link) { link.focus({ preventScroll: true }); link.scrollIntoView({ block: 'center', behavior: 'instant' }); }
  }

  disconnectedCallback() {
    this.events?.abort();
    this.requests?.abort();
    this.observer?.disconnect();
    clearTimeout(this.saveTimer);
    cancelAnimationFrame(this.frame);
    this.events = null;
  }
}

if (!customElements.get('akbar-infinite-pagination')) {
  customElements.define('akbar-infinite-pagination', AkbarInfinitePagination);
}
